<script lang="ts">
  /**
   * MobileDaysAgenda — Timeline › Days on phones (plans/mobile-v2.md §3.3): a week
   * strip, a carousel of the week's projects and one agenda scroll, in place of the
   * desktop week-rows × 7-day grid. It reads the grid's data: the visible weeks,
   * the events and the scheduled projects.
   *
   * - The strip shows the week at the top of the agenda (with several weeks visible,
   *   it follows the scroll). ‹ › and a horizontal swipe change the period through
   *   the Dashboard, so the URL changes as it does on desktop. Tapping a day scrolls
   *   the agenda to it.
   * - A tap on an event opens the event sheet; "+ Add" and the "+" button open it to
   *   create, on that day / the selected day.
   */
  import { onMount, untrack } from 'svelte';
  import type { CalendarEvent, Project } from '../../lib/types';
  import { eventColor } from '../../lib/eventColors';
  import { getCurrentWeekKey, formatEventTimeShort, getTodayStr } from '../../lib/utils';
  import { timelinePanelProjects } from '../../lib/projectLanesUtils';
  import { openEventModal, openEventModalForEdit } from '../../lib/stores/ui';
  import {
    activeWeekIndex,
    buildAgendaWeeks,
    eventDots,
    eventTypeLabel,
    initialWeekKey,
    projectInWeek,
    type AgendaDay,
  } from '../../lib/mobileDays';
  import Icon from '../Icon.svelte';
  import FabButton from './FabButton.svelte';
  import MobileDaysProjectCard from './MobileDaysProjectCard.svelte';

  interface Props {
    visibleWeeks: string[];
    events: CalendarEvent[];
    currentWeek: string;
    /** All scheduled planned/active projects, independent of the visible range. */
    allProjects?: Project[];
    /** ‹ › and the swipe: the Dashboard's period navigation. */
    onPrevious: () => void;
    onNext: () => void;
    /** "All N ›": the period's projects in the Focus view. */
    onShowAllProjects?: () => void;
  }

  let {
    visibleWeeks,
    events,
    currentWeek,
    allProjects = [],
    onPrevious,
    onNext,
    onShowAllProjects,
  }: Props = $props();

  const SWIPE_MIN_PX = 48;

  let today = $derived(getTodayStr());
  let weeks = $derived(buildAgendaWeeks(visibleWeeks, events, today));
  let showWeekHeaders = $derived(weeks.length > 1);

  // The agenda opens on today's week. Tracking the scroll afterwards is event-driven.
  let activeKey = $state(untrack(() => initialWeekKey(visibleWeeks, currentWeek)));
  let activeWeek = $derived(weeks.find((week) => week.weekKey === activeKey) ?? weeks[0]);
  let stripDays = $derived(activeWeek?.days ?? []);

  // A tapped strip day is the target of the "+" button.
  let selectedDate = $state<string | null>(null);
  let addDate = $derived.by(() => {
    const dates = stripDays.map((day) => day.date);
    if (selectedDate && dates.includes(selectedDate)) return selectedDate;
    return dates.includes(today) ? today : (dates[0] ?? today);
  });

  // Projects of the strip's week; "All N" counts the whole visible period.
  let panelProjects = $derived(timelinePanelProjects(allProjects));
  let weekProjects = $derived(
    activeWeek ? panelProjects.filter((p) => projectInWeek(p, activeWeek.weekKey)) : [],
  );
  let periodProjectCount = $derived(
    panelProjects.filter((p) => visibleWeeks.some((weekKey) => projectInWeek(p, weekKey))).length,
  );
  let projectsHeading = $derived(
    activeWeek && activeWeek.weekKey !== getCurrentWeekKey()
      ? `Projects · W${activeWeek.weekNumber}`
      : "This week's projects",
  );

  let scroller: HTMLDivElement | undefined = $state();
  let frame = 0;

  function headerHeight(): number {
    return scroller?.querySelector<HTMLElement>('.week-head')?.offsetHeight ?? 0;
  }

  function onScroll() {
    if (!showWeekHeaders || frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!scroller) return;
      const tops = [...scroller.querySelectorAll<HTMLElement>('.week')].map((el) => el.offsetTop);
      const week = weeks[activeWeekIndex(tops, scroller.scrollTop, headerHeight())];
      if (week && week.weekKey !== activeKey) activeKey = week.weekKey;
    });
  }

  function scrollTo(el: HTMLElement | null | undefined, smooth: boolean) {
    if (!el || !scroller) return;
    scroller.scrollTo?.({
      top: Math.max(0, el.offsetTop - (el.matches('.day-row') ? headerHeight() : 0)),
      behavior: smooth ? 'smooth' : 'instant',
    });
  }

  function jumpToDay(date: string) {
    selectedDate = date;
    scrollTo(scroller?.querySelector<HTMLElement>(`[data-date="${date}"]`), true);
  }

  /** Scrolls today's week into view. False when today is not in the visible period. */
  export function scrollToToday(): boolean {
    const week = weeks.find((w) => w.days.some((day) => day.isToday));
    if (!week) return false;
    selectedDate = null;
    activeKey = week.weekKey;
    scrollTo(scroller?.querySelector<HTMLElement>(`[data-week="${week.weekKey}"]`), true);
    return true;
  }

  onMount(() => {
    // With several weeks, open on today's week rather than the first row.
    if (showWeekHeaders) {
      scrollTo(scroller?.querySelector<HTMLElement>(`[data-week="${activeKey}"]`), false);
    }
    return () => cancelAnimationFrame(frame);
  });

  // Horizontal swipe on the strip. Vertical drags stay with the browser (touch-action).
  let swipeStart: { x: number; y: number } | null = null;
  let swiped = false;

  function onPointerDown(event: PointerEvent) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    swipeStart = { x: event.clientX, y: event.clientY };
    swiped = false;
  }

  function onPointerUp(event: PointerEvent) {
    if (!swipeStart) return;
    const dx = event.clientX - swipeStart.x;
    const dy = event.clientY - swipeStart.y;
    swipeStart = null;
    if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    swiped = true;
    if (dx < 0) onNext();
    else onPrevious();
  }

  // The click that ends a swipe would otherwise tap the day under the finger.
  function onClickCapture(event: MouseEvent) {
    if (!swiped) return;
    swiped = false;
    event.stopPropagation();
    event.preventDefault();
  }

  function dayLabel(day: AgendaDay): string {
    const count = day.events.length;
    return `${day.weekday}, ${day.monthLabel} ${day.dayOfMonth}${
      count ? `, ${count} event${count === 1 ? '' : 's'}` : ''
    }`;
  }

  function eventMeta(item: { event: CalendarEvent; dayNumber: number; totalDays: number }) {
    return [
      formatEventTimeShort(item.event),
      item.totalDays > 1 ? `Day ${item.dayNumber}/${item.totalDays}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }
</script>

<div class="agenda">
  <div class="strip">
    <button type="button" class="arrow" aria-label="Previous period" onclick={onPrevious}>
      <Icon name="chevron-left" size={20} strokeWidth={1.75} />
    </button>
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="days"
      role="group"
      aria-label={activeWeek ? `Week ${activeWeek.weekNumber}, ${activeWeek.rangeLabel}` : 'Week'}
      onpointerdown={onPointerDown}
      onpointerup={onPointerUp}
      onpointercancel={() => (swipeStart = null)}
      onclickcapture={onClickCapture}
    >
      {#each stripDays as day (day.date)}
        <button
          type="button"
          class="day"
          class:today={day.isToday}
          class:selected={selectedDate === day.date}
          aria-label={dayLabel(day)}
          aria-current={day.isToday ? 'date' : undefined}
          onclick={() => jumpToDay(day.date)}
        >
          <span class="dow">{day.weekday}</span>
          <span class="num">{day.dayOfMonth}</span>
          <span class="dots" aria-hidden="true">
            {#each eventDots(day.events) as color, i (i)}<i style:background={color}></i>{/each}
          </span>
        </button>
      {/each}
    </div>
    <button type="button" class="arrow" aria-label="Next period" onclick={onNext}>
      <Icon name="chevron-right" size={20} strokeWidth={1.75} />
    </button>
  </div>

  <section class="projects" aria-label="Projects">
    <div class="section-head">
      <h2>{projectsHeading}</h2>
      {#if periodProjectCount > 0 && onShowAllProjects}
        <button type="button" class="all" onclick={onShowAllProjects}>
          All {periodProjectCount} ›
        </button>
      {/if}
    </div>
    {#if weekProjects.length}
      <div class="carousel" role="group" aria-label="Projects this week">
        {#each weekProjects as project (project.id)}
          <MobileDaysProjectCard {project} weekKey={activeWeek.weekKey} />
        {/each}
      </div>
    {:else}
      <p class="no-projects">No projects scheduled this week</p>
    {/if}
  </section>

  <div class="scroller" class:multi-week={showWeekHeaders} bind:this={scroller} onscroll={onScroll}>
    {#each weeks as week (week.weekKey)}
      <section class="week" data-week={week.weekKey} aria-label="Week {week.weekNumber}">
        {#if showWeekHeaders}
          <h3 class="week-head">
            <span class="wk">W{week.weekNumber}</span>
            <span>{week.rangeLabel}</span>
          </h3>
        {/if}
        {#each week.days as day (day.date)}
          <div class="day-row" class:today={day.isToday} data-date={day.date}>
            <div class="date">
              <span class="dow">{day.weekday}</span>
              <span class="n">{day.dayOfMonth}</span>
              {#if day.dayOfMonth === 1}<span class="mon">{day.monthLabel}</span>{/if}
            </div>
            <div class="items">
              {#each day.events as item (item.event.id)}
                {@const meta = eventMeta(item)}
                <button
                  type="button"
                  class="ev"
                  style:--c={eventColor(item.event.type)}
                  onclick={() => openEventModalForEdit(item.event)}
                >
                  <span class="bar" aria-hidden="true"></span>
                  <span class="text">
                    <span class="t">{item.event.title}</span>
                    {#if meta}<span class="m">{meta}</span>{/if}
                  </span>
                  <span class="k">{eventTypeLabel(item.event.type)}</span>
                </button>
              {:else}
                <p class="empty">
                  Nothing scheduled ·
                  <button
                    type="button"
                    class="add"
                    aria-label="Add event on {day.weekday}, {day.monthLabel} {day.dayOfMonth}"
                    onclick={() => openEventModal(day.date)}
                  >
                    + Add
                  </button>
                </p>
              {/each}
            </div>
          </div>
        {/each}
      </section>
    {/each}
  </div>

  <FabButton label="Add event" onClick={() => openEventModal(addDate)} />
</div>

<style>
  .agenda {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    background: #f9fafb;
  }

  /* Week strip */
  .strip {
    display: flex;
    flex: none;
    align-items: center;
    padding: 4px 2px 6px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
  }

  .arrow {
    position: relative;
    display: grid;
    flex: none;
    place-items: center;
    width: 32px;
    height: 44px;
    padding: 0;
    color: #6b7280;
    background: none;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* 44px wide hit area; it reaches 6px over the neighbouring day. */
  .arrow::after {
    content: '';
    position: absolute;
    inset: 0 -6px;
  }

  .arrow:active {
    color: #111827;
  }

  /* Narrow phones: give the seven days at least 44px each. */
  @media (max-width: 380px) {
    .arrow {
      width: 24px;
    }

    .arrow::after {
      inset: 0 -10px;
    }
  }

  .days {
    display: flex;
    flex: 1;
    min-width: 0;
    touch-action: pan-y;
  }

  .strip .day {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 0;
    min-height: 44px;
    padding: 4px 0 5px;
    font: inherit;
    color: #111827;
    background: none;
    border: 0;
    border-radius: 12px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .strip .day:active {
    background: #f3f4f6;
  }

  .strip .dow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: #6b7280;
    text-transform: uppercase;
  }

  .strip .num {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    font-size: 18px;
    font-weight: 600;
    border-radius: 50%;
  }

  .strip .day.today .dow {
    color: #2563eb;
  }

  .strip .day.today .num {
    color: #fff;
    background: #2563eb;
  }

  .strip .day.selected .num {
    box-shadow: 0 0 0 2px #93c5fd;
  }

  .dots {
    display: flex;
    gap: 3px;
    height: 6px;
  }

  .dots i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
  }

  /* Scrolling body: only the agenda scrolls, the strip and the projects stay put. */
  .scroller {
    position: relative;
    flex: 1;
    min-height: 0;
    padding-bottom: 96px; /* room for the "+" button */
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .projects {
    flex: none;
    padding-top: 2px;
    border-bottom: 1px solid #e5e7eb;
  }

  /* The last week can always scroll to the top, so the strip never falls back a week. */
  .multi-week .week:last-child {
    min-height: 100%;
  }

  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 36px;
    padding: 0 16px;
  }

  .section-head h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #6b7280;
    text-transform: uppercase;
  }

  .all {
    height: 44px;
    margin: -4px 0; /* the 44px hit area overlaps the 36px heading row */
    padding: 0 0 0 12px;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: #2563eb;
    background: none;
    border: 0;
    cursor: pointer;
  }

  .carousel {
    display: flex;
    gap: 10px;
    padding: 2px 12px 8px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    scroll-padding-inline: 12px;
    scrollbar-width: none;
  }

  .carousel::-webkit-scrollbar {
    display: none;
  }

  .no-projects {
    margin: 0;
    padding: 4px 16px 12px;
    font-size: 13.5px;
    color: #9ca3af;
  }

  /* Agenda */
  .week-head {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    gap: 8px;
    align-items: baseline;
    margin: 0;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    background: #f3f4f6;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
  }

  .week-head .wk {
    font-weight: 700;
    color: #2563eb;
  }

  .day-row {
    display: flex;
    gap: 12px;
    padding: 10px 16px 4px;
  }

  .date {
    display: flex;
    flex: none;
    flex-direction: column;
    align-items: center;
    width: 44px;
    text-align: center;
  }

  .date .dow {
    font-size: 12px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
  }

  .date .n {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.1;
    color: #111827;
  }

  .date .mon {
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
  }

  .day-row.today .dow,
  .day-row.today .n {
    color: #2563eb;
  }

  .items {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    padding-top: 2px;
  }

  .ev {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 48px;
    padding: 8px 12px;
    text-align: left;
    font: inherit;
    color: #111827;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .ev:active {
    background: #f9fafb;
  }

  .ev .bar {
    align-self: stretch;
    flex: none;
    width: 4px;
    background: var(--c);
    border-radius: 2px;
  }

  .ev .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  .ev .t {
    font-size: 15px;
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  .ev .m,
  .ev .k {
    font-size: 12px;
    color: #6b7280;
  }

  .ev .k {
    flex: none;
  }

  .empty {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 0;
    font-size: 13.5px;
    color: #9ca3af;
  }

  .add {
    min-height: 44px;
    padding: 0 8px;
    font: inherit;
    font-weight: 600;
    color: #2563eb;
    background: none;
    border: 0;
    cursor: pointer;
  }
</style>
